/**
 * Smart call-status correction helpers.
 *
 * Le backend StatusWebhook tranche deja l'outcome via answeredBy + duree
 * (cf. TwilioService.ts). Cette fonction NE DOIT PAS reecrire 'no_answer'
 * en 'contacted' a l'affichage — sinon on contredit le verdict strict
 * humain du backend (introduit 2026-05-13 v3).
 *
 * Bug observe 2026-05-13 : Alice Lecomte avait call_outcome='no_answer'
 * en BDD mais affichait "Connecte" dans le tableau parce qu'elle avait
 * connected_at + duration=8s → l'ancienne logique forcait 'contacted'.
 * C'etait exactement le type de faux positif que le fix MIN_CONTACT_DURATION
 * + answeredBy=='human' cherchait a eliminer cote BDD.
 *
 * Nouvelle regle : on retourne le raw outcome du backend, point. Une seule
 * correction tres conservatrice survit : si duree >= 120s (2 minutes)
 * ET outcome 'no_answer', on suppose une vraie conversation (l'agent a
 * oublie de qualifier). Tout en-dessous est laisse tel quel.
 */

interface LegacyCallRecord {
  call_outcome?: string | null;
  outcome?: string | null;
  call_status?: string | null;
  duration_seconds?: number | null;
  connected_at?: string | Date | null;
  meeting_booked?: boolean | null;
}

/**
 * RDV booké = drapeau séparé du statut d'appel (2026-05-21). Un prospect peut
 * être « contacté » ET avoir un rendez-vous.
 *
 * 🚨 `call_outcome` ET `outcome` sont lus, et aucun des deux n'est de trop :
 * DEUX collections portent un résultat d'appel sous deux noms. Les appels du
 * numéroteur (`parallel_calls`) disent `call_outcome` ; le journal d'appels
 * du back-office (`call_logs`) dit `outcome`. Ces deux fonctions servent aux
 * deux, sur une trentaine d'écrans, d'où la lecture des deux noms.
 *
 * Vérifié le 2026-09-30 : sur les 1470 appels du numéroteur en production,
 * zéro porte `outcome` ; et le schéma de `call_logs` n'a pas `call_outcome`.
 * Retirer l'un des deux casserait donc la moitié des écrans en silence.
 *
 * Le vrai défaut est en amont — deux vocabulaires pour la même chose — et il
 * se corrige en renommant une collection, pas en élaguant ici.
 */
export function hasMeetingBooked(call: Pick<LegacyCallRecord, 'call_outcome' | 'outcome'> & { meeting_booked?: boolean | null } | null | undefined): boolean {
  if (!call) return false;
  if (call.meeting_booked === true) return true;
  return (call.call_outcome || call.outcome) === 'meeting_booked';
}

export function getEffectiveOutcome(call: LegacyCallRecord | null | undefined): string {
  if (!call) return '';

  const raw = call.call_outcome || call.outcome || call.call_status || '';
  const duration = Number(call.duration_seconds || 0);

  // Garde-fou unique : appel > 2 minutes classe 'no_answer' = bug evident
  // (l'agent a parle 2+ min puis a quitte sans qualifier). On corrige.
  if (duration >= 120 && raw === 'no_answer') {
    return 'contacted';
  }

  return raw;
}
